import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth';
import { useListRoles } from 'src/features/roles/api/useListRoles';
import CreateRoleFormModal from 'src/features/roles/components/forms/CreateRoleFormModal';
import CellRoleActions from 'src/features/roles/components/tables/CellRoleActions';
import { rolesMessages } from 'src/features/roles/utils/messages';
import { Role } from 'src/features/roles/utils/types';
import { userMessages } from 'src/features/users/utils/messages';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import {
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { useTitleStore } from 'src/stores/useTitleStore';
import { generalMessages } from 'src/utils/messages';

const RolesTable = () => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(rolesMessages.roles));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const permissionManageRoles = useHasPermissions([PERMISSION.MANAGE_ROLES]);

    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });

    const { data, isLoading, isError } = useListRoles({
        parkingId,
        ...queryPaginationOptions,
        ...querySortingOptions,
        ...querySearchOptions,
        config: { enabled: !!parkingId },
    });

    const columnHelper = createColumnHelper<Role>();

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(userMessages.name),
            },
        }),
        ...(permissionManageRoles
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: CellRoleActions,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<Role, any>
            isLoading={isLoading}
            isError={isError}
            columns={columns}
            data={data}
            search={search}
            sorting={sorting}
            pagination={pagination}
            semantics={{
                title: formatMessage(rolesMessages.roles),
                entryName: formatMessage(rolesMessages.role).toLowerCase(),
                entryNames: formatMessage(rolesMessages.roles).toLowerCase(),
                placeholder: `${formatMessage(generalMessages.name)}`,
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageRoles && { newEntry: CreateRoleFormModal }),
            }}
        />
    );
};

export default RolesTable;
